import { Link, useLocation } from 'react-router-dom';
import logo from '../images/logo.png';
import { HiMenuAlt1 } from 'react-icons/hi';
import { MdClose } from 'react-icons/md';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { FaFacebook, FaInstagram, FaYoutube } from 'react-icons/fa';
import { FaXTwitter, FaTiktok } from 'react-icons/fa6';
import { API_BASE_URL, FILE_BASE_URL, SITE_BASE_URL } from '../apiConfig';
import useBreakpoint from '../hooks/useBreakpoint';

// ─── Design Tokens ───────────────────────────────────────────────────────────
const TOPBAR_BG = '#040960';   // Darker navy topbar
const NAV_BG    = '#060D8A';   // Primary navy navbar
const ACCENT    = 'var(--yellow)';   // Yellow highlight
const WHITE     = '#ffffff';
// ─────────────────────────────────────────────────────────────────────────────

function Header() {
    const location = useLocation();
    const { isMobile, isDesktop } = useBreakpoint();

    const [announcement, setAnnounement] = useState('');
    const [sponsor, setSponsor]         = useState('');
    const [sponsorLink, setSponsorLink] = useState('');
    const [socialLink, setSocialLink]   = useState([]);
    const [menuOpen, setMenuOpen]       = useState(false);

    // ── API calls (all unchanged) ─────────────────────────────────────────────
    useEffect(() => {
        let alive = true;
        axios.get(`${API_BASE_URL}/socials`)
            .then(res => {
                if (!alive || !res.data.socials) return;
                const s = res.data.socials;
                setSocialLink([s.facebook, s.twitter, s.youtube, s.instagram]);
            })
            .catch(() => {});
        return () => { alive = false; };
    }, []);

    useEffect(() => {
        let alive = true;
        const lang = document.getElementById('language')?.value ?? '1';

        axios.get(`${API_BASE_URL}/announcement/${lang}`)
            .then(res => {
                if (!alive || !res.data.announcement) return;
                const a = res.data.announcement;
                setAnnounement([a.text, a.link]);
            })
            .catch(() => {});

        axios.get(`${API_BASE_URL}/sponsors`)
            .then(res => {
                if (!alive || !res.data.sponsors) return;
                setSponsor(res.data.sponsors.image);
                setSponsorLink(res.data.sponsors.link);
            })
            .catch(() => {});

        return () => { alive = false; };
    }, []);
    // ─────────────────────────────────────────────────────────────────────────

    const isActive = path =>
        path === '/'
            ? location.pathname === '/'
            : location.pathname.startsWith(path);

    const navLinks = [
        { label: 'Home',           to: '/' },
        { label: 'Divisions',      to: '/leagues' },
        { label: 'BTCL 100 Club',  to: '/btcl-100-club' },
        { label: 'Playing Condition', to: '/playing-condition' },
        { label: 'Contact',        to: '/contact' },
    ];

    const moreLinks = [
        { label: 'News',           to: '/news-list' },
        { label: 'Videos',         to: '/videos-list' },
        { label: 'Notices',        to: '/notices-list' },
        { label: 'About BTCL',     to: '/about-btcl' },
        { label: 'Constitution',   to: '/constitution' },
        { label: 'Discipline',     to: '/discipline' },
        { label: 'Committee',      to: '/committee' },
        { label: 'Accounting',     to: '/accounting' },
    ];

    return (
        <header style={{ width: '100%', position: 'fixed', top: 0, zIndex: 1000, boxShadow: '0 2px 12px rgba(0,0,0,0.4)' }}>

            {/* ── TOP BAR (Dark Navy) ── */}
            <div style={{ 
                background: TOPBAR_BG, 
                minHeight: '44px', 
                height: isMobile ? 'auto' : '44px',
                display: 'flex', 
                alignItems: 'center',
                padding: isMobile ? '8px 0' : '0'
            }}>
                <div style={{ 
                    maxWidth: '1260px', 
                    width: '100%',
                    margin: '0 auto', 
                    padding: '0 24px', 
                    display: 'flex',
                    flexDirection: !isDesktop ? 'column' : 'row',
                    justifyContent: !isDesktop ? 'center' : 'space-between', 
                    alignItems: 'center',
                    gap: !isDesktop ? '10px' : '0',
                    boxSizing: 'border-box'
                }}>
                    
                    {isDesktop ? (
                        <>
                            {/* Socials - Desktop */}
                            <nav style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                {[
                                    { href: socialLink[0], Icon: FaFacebook },
                                    { href: socialLink[3], Icon: FaInstagram },
                                    { href: socialLink[1], Icon: FaXTwitter  },
                                    { href: socialLink[2], Icon: FaYoutube   },
                                    { href: socialLink[3], Icon: FaTiktok    },
                                ].map(({ href, Icon }, i) => (
                                    <a key={i} href={href || '#'} target="_blank" rel="noreferrer" className="top-social-icon">
                                        <Icon size={15} color={WHITE} />
                                    </a>
                                ))}
                            </nav>

                            {/* Announcement - Desktop */}
                            <div style={{ flex: 1, margin: '0 32px', overflow: 'hidden' }}>
                                <marquee style={{ color: WHITE, fontSize: '13px', fontWeight: 500, fontFamily: 'var(--fontBody)' }}>
                                    {Array.isArray(announcement) ? announcement[0] : announcement}
                                </marquee>
                            </div>

                            {/* Sponsor - Desktop */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: WHITE, fontWeight: 500, flexShrink: 0 }}>
                                <span style={{ whiteSpace: 'nowrap' }}>Proud Sponsor:</span>
                                {sponsor && (
                                    <a href={sponsorLink || '#'} target="_blank" rel="noreferrer" style={{ background: WHITE, borderRadius: '2px', padding: '2px 8px', height: '24px', display: 'flex', alignItems: 'center' }}>
                                        <img src={`${FILE_BASE_URL}/${sponsor}`} height="16px" alt="sponsor" />
                                    </a>
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Mobile Top Row: Socials & Sponsor */}
                            <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <nav style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    {[
                                        { href: socialLink[0], Icon: FaFacebook },
                                        { href: socialLink[3], Icon: FaInstagram },
                                        { href: socialLink[1], Icon: FaXTwitter  },
                                        { href: socialLink[2], Icon: FaYoutube   },
                                        { href: socialLink[3], Icon: FaTiktok    },
                                    ].map(({ href, Icon }, i) => (
                                        <a key={i} href={href || '#'} target="_blank" rel="noreferrer" className="top-social-icon">
                                            <Icon size={14} color={WHITE} />
                                        </a>
                                    ))}
                                </nav>

                                {sponsor && (
                                    <a href={sponsorLink || '#'} target="_blank" rel="noreferrer" style={{ background: WHITE, borderRadius: '2px', padding: '2px 8px', height: '22px', display: 'flex', alignItems: 'center' }}>
                                        <img src={`${FILE_BASE_URL}/${sponsor}`} height="14px" alt="sponsor" />
                                    </a>
                                )}
                            </div>

                            {/* Mobile Bottom Row: Announcement */}
                            <div style={{ width: '100%', overflow: 'hidden', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '8px' }}>
                                <marquee style={{ color: WHITE, fontSize: '11px', fontWeight: 500, fontFamily: 'var(--fontDisplay)', opacity: 0.9 }}>
                                    {Array.isArray(announcement) ? announcement[0] : announcement}
                                </marquee>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* ── NAVBAR (Primary Navy) ── */}
            <div style={{ background: NAV_BG, height: '80px', display: 'flex', alignItems: 'center' }}>
                <div style={{ maxWidth: 'var(--maxWidth)', margin: '0 auto', padding: '8px 24px', width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '25px', boxSizing: 'border-box' }}>

                    {/* Logo — left, flex-grow 1 */}
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                        <Link to="/" onClick={() => window.scrollTo(0, 0)} style={{ display: 'flex', alignItems: 'center' }}>
                            <img src={logo} height="64px" alt="BTCL logo" />
                        </Link>
                    </div>

                    {/* Desktop Navigation — center */}
                    <nav className="nav-desktop" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <ul style={{ display: 'flex', listStyle: 'none', margin: 0, padding: 0, gap: '24px', alignItems: 'center' }}>
                            {navLinks.map(({ label, to }) => (
                                <li key={to} style={{ display: 'flex', alignItems: 'center' }}>
                                    <Link
                                        to={to}
                                        className={`nav-link ${isActive(to) ? 'nav-active' : ''}`}
                                        style={{ display: 'flex', alignItems: 'center', padding: '4px 8px' }}
                                        onClick={() => window.scrollTo(0, 0)}
                                    >
                                        {label}
                                    </Link>
                                </li>
                            ))}

                            {/* More Dropdown */}
                            <li className="nav-item-more" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                <div className="nav-link" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 8px', cursor: 'pointer' }}>
                                    More
                                    <svg viewBox="0 0 10 6" width="12" height="8" fill={WHITE}><path d="M0 0l5 6 5-6z"/></svg>
                                </div>
                                <ul className="nav-dropdown" style={{ position: 'absolute', top: '100%', left: 0, background: NAV_BG, minWidth: '180px', boxShadow: '0 8px 24px rgba(0,0,0,0.4)', listStyle: 'none', padding: 0, borderTop: `2px solid ${ACCENT}`, visibility: 'hidden', opacity: 0, transform: 'translateY(-6px)', transition: 'all 0.25s' }}>
                                    {moreLinks.map(({ label, to }) => (
                                        <li key={to}>
                                            <Link to={to} style={{ display: 'block', padding: '11px 18px', fontFamily: "'Oswald', sans-serif", fontSize: '16px', fontWeight: 500, color: WHITE, textDecoration: 'none', textTransform: 'uppercase' }} onMouseEnter={(e) => e.target.style.color = ACCENT} onMouseLeave={(e) => e.target.style.color = WHITE} onClick={() => window.scrollTo(0, 0)}>
                                                {label}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </li>
                        </ul>
                    </nav>

                    {/* CTA Button — right, flex-grow 1, right-aligned */}
                    <div className="nav-desktop" style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                        <a href={`${SITE_BASE_URL}/team/login`} target="_blank" rel="noreferrer" id="team-login-btn-new">
                            Team Login
                        </a>
                    </div>

                    {/* Mobile Hamburger */}
                    <div className="hamburger-container" style={{ display: 'none' }}>
                        {menuOpen
                            ? <MdClose onClick={() => setMenuOpen(false)} style={{ width: 30, height: 30, color: WHITE, cursor: 'pointer' }} />
                            : <HiMenuAlt1 onClick={() => setMenuOpen(true)} style={{ width: 30, height: 30, color: WHITE, cursor: 'pointer' }} />
                        }
                    </div>
                </div>
            </div>

            {/* CSS for Dropdown Hover */}
            <style>{`
                .nav-item-more:hover .nav-dropdown {
                    visibility: visible !important;
                    opacity: 1 !important;
                    transform: translateY(0) !important;
                }
                @media (max-width: 1024px) {
                    .nav-desktop { display: none !important; }
                    .hamburger-container { display: flex !important; }
                }
            `}</style>

            {/* ── MOBILE MENU ── */}
            <div className="popup-navbar-content" style={{ right: menuOpen ? 0 : '-100%' }}>
                <ul style={{ padding: '80px 0 20px 0', margin: 0, listStyle: 'none' }}>
                    {[...navLinks, ...moreLinks].map(({ label, to }) => (
                        <li key={to}>
                            <Link to={to} style={{ display: 'block', padding: '13px 24px', fontSize: '14px', fontWeight: 600, color: WHITE, textDecoration: 'none', textTransform: 'uppercase', borderLeft: '3px solid transparent' }} onClick={() => { setMenuOpen(false); window.scrollTo(0, 0); }}>
                                {label}
                            </Link>
                        </li>
                    ))}
                    <li style={{ padding: '20px 24px' }}>
                        <a href={`${SITE_BASE_URL}/team/login`} style={{ display: 'block', textAlign: 'center', padding: '12px', background: WHITE, color: NAV_BG, borderRadius: '4px', textDecoration: 'none', fontWeight: 700 }}>
                            Team Login
                        </a>
                    </li>
                </ul>
            </div>

            {/* Language Hidden Guard */}
            <div style={{ display: 'none' }}><select id="language"><option value="1">EN</option></select></div>
        </header>
    );
}

export default Header;
